// Ders durumunun istemcideki kopyası. Sunucu mesajları buraya uygulanır; çizim katmanı
// bu depoya doğrudan abone olur (React yeniden çizimi gerekmeden).
import type {
  Annotation,
  InkAnnotation,
  LessonInfo,
  LessonSettings,
  Participant,
  ServerMsg,
  ShapePreview,
} from '../../shared/protocol';
import { createStore } from './store';
import type { ConnStatus } from './connection';

export interface LaserTrail {
  page: number;
  points: { x: number; y: number; t: number }[];
  last: number;
}

export interface PreviewEntry extends ShapePreview {
  at: number;
}

export interface LessonState {
  status: ConnStatus;
  everOnline: boolean;
  fatal: string | null;
  self: Participant | null;
  lesson: LessonInfo | null;
  settings: LessonSettings;
  participants: Participant[];
  /** Sayfa numarası → o sayfadaki çizimler (geliş sırasıyla; çizim sırası budur). */
  pages: Record<number, Annotation[]>;
  /** Başkalarının hâlâ çizmekte olduğu kalem izleri. */
  drafts: Record<string, InkAnnotation>;
  previews: Record<string, PreviewEntry>;
  lasers: Record<string, LaserTrail>;
  toast: { id: number; text: string } | null;
}

const initial = (): LessonState => ({
  status: 'connecting',
  everOnline: false,
  fatal: null,
  self: null,
  lesson: null,
  settings: { studentsCanDraw: false, followLocked: false, teacherPage: 1 },
  participants: [],
  pages: {},
  drafts: {},
  previews: {},
  lasers: {},
  toast: null,
});

export const lessonStore = createStore<LessonState>(initial());
const pageIndex = new Map<string, number>();
let toastSeq = 0;

function addAnnotations(list: readonly Annotation[]) {
  if (!list.length) return;
  lessonStore.set((s) => {
    const pages = { ...s.pages };
    const copied = new Set<number>();
    const pageList = (p: number) => {
      if (!copied.has(p)) {
        pages[p] = pages[p] ? [...pages[p]] : [];
        copied.add(p);
      }
      return pages[p];
    };
    let drafts = s.drafts;
    for (const a of list) {
      if (drafts[a.id]) {
        drafts = { ...drafts };
        delete drafts[a.id];
      }
      const prev = pageIndex.get(a.id);
      if (prev !== undefined) {
        const arr = pageList(prev);
        const i = arr.findIndex((x) => x.id === a.id);
        if (i >= 0) {
          if (prev === a.page) {
            arr[i] = a;
            continue;
          }
          arr.splice(i, 1);
        }
      }
      pageList(a.page).push(a);
      pageIndex.set(a.id, a.page);
    }
    return { pages, drafts };
  });
}

function removeAnnotations(ids: readonly string[]) {
  const byPage = new Map<number, Set<string>>();
  for (const id of ids) {
    const p = pageIndex.get(id);
    if (p === undefined) continue;
    pageIndex.delete(id);
    let set = byPage.get(p);
    if (!set) byPage.set(p, (set = new Set()));
    set.add(id);
  }
  if (!byPage.size) return;
  lessonStore.set((s) => {
    const pages = { ...s.pages };
    for (const [p, set] of byPage) pages[p] = (pages[p] ?? []).filter((a) => !set.has(a.id));
    return { pages };
  });
}

export const lesson = {
  reset() {
    pageIndex.clear();
    lessonStore.set(initial());
  },

  status(status: ConnStatus, detail?: string) {
    if (status === 'fatal') lessonStore.set({ status, fatal: detail ?? 'Bağlantı kurulamadı.' });
    else lessonStore.set((s) => ({ status, everOnline: s.everOnline || status === 'online' }));
  },

  toast(text: string) {
    lessonStore.set({ toast: { id: ++toastSeq, text } });
  },

  add: addAnnotations,
  remove: removeAnnotations,

  apply(msg: ServerMsg) {
    switch (msg.t) {
      case 'init': {
        pageIndex.clear();
        const pages: Record<number, Annotation[]> = {};
        for (const a of msg.annotations) {
          (pages[a.page] ??= []).push(a);
          pageIndex.set(a.id, a.page);
        }
        const drafts: Record<string, InkAnnotation> = {};
        for (const d of msg.drafts) drafts[d.id] = d;
        lessonStore.set({
          self: msg.self,
          lesson: msg.lesson,
          settings: msg.settings,
          participants: msg.participants,
          pages,
          drafts,
          previews: {},
          lasers: {},
        });
        return;
      }
      case 'ink:start':
        lessonStore.set((s) => ({ drafts: { ...s.drafts, [msg.a.id]: msg.a } }));
        return;
      case 'ink:append':
        lessonStore.set((s) => {
          const d = s.drafts[msg.id];
          if (!d) return null;
          return { drafts: { ...s.drafts, [msg.id]: { ...d, points: d.points.concat(msg.points) } } };
        });
        return;
      case 'ink:end':
        addAnnotations([msg.a]);
        return;
      case 'ann:add':
        addAnnotations(msg.list);
        return;
      case 'ann:delete':
        removeAnnotations(msg.ids);
        return;
      case 'settings':
        lessonStore.set({ settings: msg.s });
        return;
      case 'participants':
        lessonStore.set({ participants: msg.list });
        return;
      case 'preview':
        lessonStore.set((s) => {
          const previews = { ...s.previews };
          if (msg.p) previews[msg.userId] = { ...msg.p, at: Date.now() };
          else if (previews[msg.userId]) delete previews[msg.userId];
          else return null;
          return { previews };
        });
        return;
      case 'laser':
        lesson.laser(msg.userId, msg.p);
        return;
      case 'denied':
        if (msg.ids?.length) {
          removeAnnotations(msg.ids);
          lessonStore.set((s) => {
            const drafts = { ...s.drafts };
            for (const id of msg.ids!) delete drafts[id];
            return { drafts };
          });
        }
        lesson.toast(msg.reason);
        return;
      case 'fatal':
        return;
    }
  },

  laser(userId: string, p: { page: number; x: number; y: number } | null) {
    lessonStore.set((s) => {
      const lasers = { ...s.lasers };
      if (!p) {
        if (!lasers[userId]) return null;
        delete lasers[userId];
        return { lasers };
      }
      const now = Date.now();
      const prev = lasers[userId];
      const trail = prev && prev.page === p.page ? prev.points.filter((q) => now - q.t < 600) : [];
      trail.push({ x: p.x, y: p.y, t: now });
      lasers[userId] = { page: p.page, points: trail.slice(-24), last: now };
      return { lasers };
    });
  },

  /** Süresi geçmiş lazer izlerini ve takılı kalmış şekil önizlemelerini temizler. */
  prune(now: number) {
    const s = lessonStore.get();
    const staleLasers = Object.entries(s.lasers).filter(([, l]) => now - l.last > 2000);
    const stalePreviews = Object.entries(s.previews).filter(([, p]) => now - p.at > 5000);
    if (!staleLasers.length && !stalePreviews.length) return;
    lessonStore.set((st) => {
      const lasers = { ...st.lasers };
      const previews = { ...st.previews };
      for (const [k] of staleLasers) delete lasers[k];
      for (const [k] of stalePreviews) delete previews[k];
      return { lasers, previews };
    });
  },
};
